//* *********************************** REACT IMPORTS ************************************
import React, { useState, memo, useEffect, useRef } from "react";
//* ********************************* EXTERNAL PACKAGES **********************************
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
//* *********************************** OUR COMPONENTS ***********************************
import DropdownOptions from "../dropdownOptions";
import Paginator from "../paginator";
import Spinner from "../spinner";
import useSearch from "hooks/useSearch";
import useOnClickOutside from "hooks/useOnClickOutside";
import {
  getRofServices,
  ensureSdkPatientAndAssessment,
  linkEvaluationToRof,
  getEvaluationRofLink,
  deleteRofAssessment,
  calcAge,
  mapGenderAtBirthForSdk,
  parseHeightCm,
  syncSdkPatientDemographics,
} from "../../utils/rofClient";
import MessageModal from "../messageModal";
import CreateEvaluationModal from "./partials/createEvaluationModal";
import { parseDateFromDatetime } from "../../utils/dataAndTime";
//* ************************************** REDUX *****************************************
import {
  getPatientEvaluations,
  createPatientEvaluation,
  deletePatientEvaluation,
  setConsentCheck,
  getPatientConsentPdf,
  getPatientHome,
  editPatientClinicalData,
  getPatientRofLink,
  setPatientRofLink,
  getPatientClinicalData,
} from "../../redux/actions/patients";
import { getEvaluationTags } from "../../redux/actions/evaluations";
import { getEvaluationModules } from "../../redux/actions/evaluationTemplates";
import { addEvaluationModule } from "../../redux/actions/patientEvaluationModule";
//* ************************************** ASSETS ****************************************
import evaluationsIcon from "../../assets/images/evaluationsModuleIcons/evaluations_white.svg";
import chevronUpIcon from "../../assets/images/evaluationsModuleIcons/chevron-up_light-green.svg";
import chevronUpSelectedIcon from "../../assets/images/evaluationsModuleIcons/chevron-up_dark-green.svg";
import chevronDownIcon from "../../assets/images/evaluationsModuleIcons/chevron-down_light-green.svg";
import chevronDownSelectedIcon from "../../assets/images/evaluationsModuleIcons/chevron-down_dark-green.svg";
import search from "../../assets/images/search.svg";
import moreBlue from "../../assets/images/more-blue.svg";
import moreBlueSelected from "../../assets/images/more-blue-selected-grey.svg";
import arrowRightIcon from "../../assets/images/arrow-right.svg";

const normalize = (s) =>
  (s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const isRofVideoModuleName = (name) => {
  const n = normalize(name);
  if (!n) return false;

  // EN
  if (n.includes("risk of fall") && n.includes("video")) return true;
  // PT
  if (n.includes("risco de queda") && n.includes("video")) return true;

  return false;
};

const mapGenderAtBirthToGenderId = (g) => {
  const x = String(g || "")
    .toLowerCase()
    .trim();
  if (x === "male" || x.startsWith("m")) return 1;
  if (x === "female" || x.startsWith("f")) return 2;
  if (x === "other" || x.startsWith("o")) return 3;
  return null;
};

const EvaluationsModule = ({
  history,
  patientId = null,
  headerText = "",
  itemsPerPage,
  isNewEvaluationsBtn = true,
  isSearchBar = true,
  isPaginator = true,
  colsToShow = ["name", "tags", "date"],
  nameTextLink = null,
  rightMenueButtons = [],
  isFullRowClickable = false,
  extraStyleClass = "",
  spinnerType = "big-blue",
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { patient_id } = useParams("patient_id");
  const dropDownDivRef = useRef(null);
  const optionsRef = useRef([]);

  const [isEvaluationListLoading, setIsEvaluationListLoading] = useState(false);

  const [selectedEvaluationId, setSelectedEvaluationId] = useState(null);
  const [deletingEvaluationId, setDeletingEvaluationId] = useState(null);
  const [showCreateEvalModal, setShowCreateEvalModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [hasAccessPermit, setHasAccessPermit] = useState(true);
  const [firstConsent, setFirstConsent] = useState(false);
  const [acceptConsentCheckbox, setAcceptConsentCheckbox] = useState(false);
  const [isConsentRequestLoading, setIsConsentRequestLoading] = useState(false);
  const [isEvaluationCreating, setIsEvaluationCreating] = useState(false);

  const patientEvaluations = useSelector(
    (state) => state.patientEvaluations.evaluations,
  );
  const patientHome = useSelector((state) => state.patientHome);
  const patientProfile = useSelector((state) => state.patientProfile);
  const hasConsent = useSelector((state) => state.patientHome.consent_check);
  const canAssignFallRisk = useSelector(
    (s) => s.institutions?.current_institution?.package_plan_id === 5 ?? false,
  );

  const pid = patient_id || patientId;
  const [prefillClinical, setPrefillClinical] = useState(null);

  const availableModules = useSelector(
    (state) => state.evaluationTemplates?.evaluationModules?.all ?? [],
  );
  console.log("patientId:", patientId);

  useEffect(() => {
    if (patientId !== null) {
      getEvaluations();
      dispatch(getPatientHome(patientId)).finally(() =>
        setAcceptConsentCheckbox(false),
      );
    }
  }, [patientId]);

  useOnClickOutside([dropDownDivRef, optionsRef], () =>
    setSelectedEvaluationId(null),
  );

  const getEvaluations = () => {
    setIsEvaluationListLoading(true);
    dispatch(
      getPatientEvaluations(patient_id ? patient_id : patientId, {
        page: searchOptions?.page,
        q: searchOptions?.q,
        order_by: searchOptions?.orderBy,
        items_per_page: searchOptions?.itemsPerPage,
      }),
    ).then(
      () => {
        setIsEvaluationListLoading(false);
        setHasAccessPermit(true);
      },
      (err) => {
        setIsEvaluationListLoading(false);
        if (err?.error?.response?.data?.msg === "cannot_access_patient") {
          setHasAccessPermit(false);
        }
      },
    );
  };

  const { searchOptions, updateSearchText, updatePage, updateOrderBy } =
    useSearch(getEvaluations, 300, "evaluations");

  const getAvailableTags = () => {
    dispatch(getEvaluationTags());
  };

  const loadAvailableModules = () =>
    dispatch(getEvaluationModules({ module_type: "all", keyword: "" }));

  const createNewEvaluation = async (
    evalName,
    evalTagsList,
    evalDate,
    evalNotes,
    fallRiskAttach,
    rofPatientOverrides,
  ) => {
    setIsEvaluationCreating(true);

    const normalizeLabel = (s) =>
      (s || "")
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .trim();

    const resolveGenderIdFromChoices = (choices, genderAtBirth) => {
      if (!Array.isArray(choices) || !choices.length) return null;

      const target = normalizeLabel(genderAtBirth);
      if (!target) return null;

      const isMaleTarget = target === "male" || target.startsWith("m");
      const isFemaleTarget = target === "female" || target.startsWith("f");
      const isOtherTarget = target === "other" || target.startsWith("o");

      const match = choices.find(([id, label]) => {
        const l = normalizeLabel(label);

        const maleLike =
          l.startsWith("m") ||
          l.includes("male") ||
          l.includes("masc") ||
          l.includes("homem");
        const femaleLike =
          l.startsWith("f") ||
          l.includes("female") ||
          l.includes("fem") ||
          l.includes("mulher");
        const otherLike =
          l.startsWith("o") || l.includes("other") || l.includes("outro");

        return (
          (isMaleTarget && maleLike) ||
          (isFemaleTarget && femaleLike) ||
          (isOtherTarget && otherLike)
        );
      });

      return match ? Number(match[0]) : null;
    };

    try {
      const pid = patient_id ? patient_id : patientId;

      const res = await dispatch(
        createPatientEvaluation(pid, {
          evaluation_name: evalName,
          tag_list: evalTagsList,
          date: evalDate,
          comments: evalNotes,
        }),
      );

      const evaluation_id = res?.payload?.data?.data?.evaluation_id;
      if (!evaluation_id)
        throw new Error("Missing evaluation_id from backend response");

      let attachedModuleId = null;
      if (fallRiskAttach?.module_id) {
        await dispatch(
          addEvaluationModule(evaluation_id, fallRiskAttach.module_id),
        );
        attachedModuleId = fallRiskAttach.module_id;
      }

      const hasRoFByTemplate = Boolean(fallRiskAttach?.template_id);
      const selectedModule =
        attachedModuleId &&
        availableModules?.find((m) => m.id === attachedModuleId);

      const isRoFVideo =
        !!selectedModule && isRofVideoModuleName(selectedModule.name);
      const willGoToRoF = isRoFVideo || hasRoFByTemplate;

      if (willGoToRoF) {
        let sdkIds = null;
        try {
          await getRofServices();

          let existingSdkPatientId = null;
          try {
            const linkRes = await dispatch(getPatientRofLink(pid));
            existingSdkPatientId =
              linkRes?.payload?.data?.data?.sdk_patient_id ?? null;
          } catch {}

          const gender_at_birth =
            rofPatientOverrides?.gender_at_birth ??
            mapGenderAtBirthForSdk(patientHome?.gender) ??
            null;

          const height_cmRaw =
            rofPatientOverrides?.height_cm ?? parseHeightCm(patientHome);
          const height_cm =
            Number.isFinite(height_cmRaw) && height_cmRaw > 0
              ? height_cmRaw
              : null;

          const age = calcAge(patientHome?.birthdate);

          try {
            const gender_id = mapGenderAtBirthToGenderId(gender_at_birth);

            const clinicalPayload = {};
            if (gender_id != null) clinicalPayload.gender_id = gender_id;
            if (height_cm != null)
              clinicalPayload.height = Math.round(height_cm);

            if (Object.keys(clinicalPayload).length > 0) {
              console.log("Updating clinical data for RoF:", clinicalPayload);
              await dispatch(editPatientClinicalData(pid, clinicalPayload));
              dispatch(getPatientHome(pid));
            }
          } catch (e) {
            console.warn("Clinical data update failed (skipping):", e);
          }

          const ensureWithIdOrCreate = async (candidateId) => {
            try {
              return await ensureSdkPatientAndAssessment({
                sdkPatientId: candidateId,
                sdkAssessmentId: null,
                patientPayload: {},
              });
            } catch (err) {
              console.error(
                "[ROF] Failed to create assessment for existing SDK patient",
                candidateId,
                err,
              );
              throw err;
            }
          };

          if (existingSdkPatientId) {
            sdkIds = await ensureWithIdOrCreate(existingSdkPatientId);
          } else {
            const hasDemographics = Boolean(
              gender_at_birth && height_cm != null && age != null,
            );
            if (hasDemographics) {
              sdkIds = await ensureSdkPatientAndAssessment({
                sdkPatientId: null,
                sdkAssessmentId: null,
                patientPayload: { age, gender_at_birth, height_cm },
              });

              try {
                await dispatch(setPatientRofLink(pid, sdkIds.sdkPatientId));
              } catch {}
            } else {
              console.warn(
                "[ROF] Missing demographics for first-time SDK creation; proceeding to UI.",
              );
            }
          }

          if (sdkIds?.sdkPatientId && sdkIds?.sdkAssessmentId) {
            await linkEvaluationToRof(evaluation_id, {
              sdkPatientId: sdkIds.sdkPatientId,
              sdkAssessmentId: sdkIds.sdkAssessmentId,
            });

            // Sync demographics in case platform data drifted
            try {
              const enrichedPatient = {
                ...patientHome,
                height: patientProfile?.height ?? patientHome?.height,
                gender_id: patientProfile?.gender_id ?? patientHome?.gender_id,
              };
              await syncSdkPatientDemographics({
                sdkPatientId: sdkIds.sdkPatientId,
                platformPatient: enrichedPatient,
              });
            } catch {}
          }
        } catch (e) {
          console.warn("ROF auto-create/link failed, continuing to UI:", e);
        } finally {
          history.push({
            pathname: `/patient/${pid}/rof_assessment/${evaluation_id}`,
            state: {
              rofLink: sdkIds
                ? {
                    sdk_patient_id: sdkIds.sdkPatientId,
                    sdk_assessment_id: sdkIds.sdkAssessmentId,
                  }
                : null,
            },
          });
        }

        return;
      }

      history.push(`/patient/${pid}/evaluations/${evaluation_id}/edit`);
    } catch (e) {
      console.error("createNewEvaluation error:", e);
    } finally {
      setIsEvaluationCreating(false);
      setShowCreateEvalModal(false);
      getEvaluations();
    }
  };

  const deleteEvaluation = async (evaluationId) => {
    const pid = patient_id ? patient_id : patientId;

    try {
      const linkForDelete = await getEvaluationRofLink(evaluationId);
      const sdkAssessmentIdDel =
        linkForDelete?.sdk_assessment_id ??
        linkForDelete?.sdkAssessmentId ??
        null;
      if (sdkAssessmentIdDel) {
        await deleteRofAssessment(sdkAssessmentIdDel);
      }
    } catch (err) {
      console.warn("[ROF] SDK assessment delete skipped:", err);
    }

    try {
      await dispatch(deletePatientEvaluation(pid, evaluationId));
    } finally {
      getEvaluations();
    }
  };

  const openEvaluation = async (evaluation) => {
    const pid = patient_id ? patient_id : patientId;

    try {
      const link = await getEvaluationRofLink(evaluation.id);
      const sdkAssessmentId =
        link?.sdk_assessment_id ?? link?.sdkAssessmentId ?? null;
      const sdkPatientId = link?.sdk_patient_id ?? link?.sdkPatientId ?? null;

      if (sdkAssessmentId) {
        if (sdkPatientId) {
          // Fetch latest clinical data so height/gender are current
          let freshClinical = null;
          try {
            const clinicalRes = await dispatch(getPatientClinicalData(pid));
            freshClinical = clinicalRes?.payload?.data?.data ?? null;
          } catch {}

          // Build enriched patient object merging home + fresh clinical data
          const enrichedPatient = {
            ...patientHome,
            height: freshClinical?.height ?? patientHome?.height,
            gender_id: freshClinical?.gender_id ?? patientHome?.gender_id,
          };

          await syncSdkPatientDemographics({
            sdkPatientId,
            platformPatient: enrichedPatient,
          });
        }
        history.push({
          pathname: `/patient/${pid}/rof_assessment/${evaluation.id}`,
          state: {
            rofLink: {
              sdk_patient_id: sdkPatientId,
              sdk_assessment_id: sdkAssessmentId,
            },
          },
        });
        return;
      }
    } catch (e) {
      console.warn("[ROF] link lookup failed, opening edit page:", e);
    }
    history.push(`/patient/${pid}/evaluations/${evaluation.id}/edit/`);
  };

  const openNewEvaluationModal = async () => {
    getAvailableTags();
    await loadAvailableModules();

    try {
      const resp = await dispatch(getPatientClinicalData(pid));
      setPrefillClinical(resp?.payload?.data?.data ?? null);
    } catch {
      setPrefillClinical(null);
    }

    setShowCreateEvalModal(true);
  };

  const handleNewEvaluationBtnClick = () => {
    if (!hasConsent) setFirstConsent(true);
    else openNewEvaluationModal();
  };

  const handleConsentCheck = () => {
    setIsConsentRequestLoading(true);
    dispatch(setConsentCheck(patient_id ? patient_id : patientId))
      .then(() => {
        setFirstConsent(false);
        openNewEvaluationModal();
      })
      .catch(() => {})
      .finally(() => setIsConsentRequestLoading(false));
  };

  const downloadConsentPdf = () => {
    dispatch(
      getPatientConsentPdf({
        name: patientHome?.name,
        birthdate: patientHome?.birthdate,
      }),
    )
      .then((response) => {
        const blob = new Blob([response.payload.data]);
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = "consent_" + (patientHome?.name ?? "patient") + ".pdf";
        link.click();
      })
      .catch(() => {});
  };

  const _renderButtons = () => {
    let buttons = [];

    rightMenueButtons.map((value) => {
      if (value === "report") {
        buttons.push({
          text: <span className='regular-text'>{t("buttons.report")}</span>,
          action: () => {
            history.push(
              `/patient/${
                patient_id ? patient_id : patientId
              }/evaluations/${selectedEvaluationId}/report`,
            );
          },
        });
      } else if (value === "edit") {
        buttons.push({
          text: <span className='regular-text'>{t("buttons.edit")}</span>,
          action: () => {
            history.push(
              `/patient/${
                patient_id ? patient_id : patientId
              }/evaluations/${selectedEvaluationId}/edit`,
            );
          },
        });
      } else if (value === "delete") {
        buttons.push({
          text: (
            <span className='regular-text warning'>{t("buttons.delete")}</span>
          ),
          action: () => {
            setDeletingEvaluationId(selectedEvaluationId);
            setSelectedEvaluationId(null);
          },
        });
      } else if (value === "view_details") {
        buttons.push({
          text: (
            <span className='regular-text'>{t("buttons.view_details")}</span>
          ),
          action: () => {
            history.push(
              `/patient/${
                patient_id ? patient_id : patientId
              }/evaluations/${selectedEvaluationId}/report`,
            );
          },
        });
      }
      return null;
    });

    return buttons;
  };

  const _renderFirstConsentModal = () => {
    return (
      <MessageModal
        hasCloseButton={true}
        handleClose={() => setFirstConsent(false)}
        headerText={t("my_area.mKinetikos.consent_title")}
        body={
          <div className='first-consent-container'>
            <div className='input-wrapper check-box'>
              <input
                disabled={false}
                name={"checkbox"}
                checked={acceptConsentCheckbox}
                onChange={() =>
                  setAcceptConsentCheckbox(!acceptConsentCheckbox)
                }
                type={"checkbox"}
              />
            </div>
            <span>
              {t("create_patient.main_data.consent_text")}{" "}
              <span
                className='primary-hover-color button'
                onClick={downloadConsentPdf}
              >
                {t("create_patient.main_data.available")}
              </span>
            </span>
          </div>
        }
        footerButtons={[
          <button
            className='transparent-button small-wider-button mr-1'
            onClick={() => setFirstConsent(false)}
          >
            <u>{t("buttons.cancel")}</u>
          </button>,
          <button
            className='secondary-button small-wider-button'
            disabled={!acceptConsentCheckbox}
            onClick={handleConsentCheck}
          >
            {isConsentRequestLoading ? (
              <div className='position-relative p-1'>
                <Spinner type={"small"} />
              </div>
            ) : (
              t("buttons.confirm")
            )}
          </button>,
        ]}
      />
    );
  };

  return (
    hasAccessPermit && (
      <>
        <div
          className={`evaluations-module-wrapper ${extraStyleClass}`}
          style={{ padding: extraStyleClass ? "0" : "" }}
        >
          <div className='evaluations-module-container'>
            <div className='row-space-between'>
              <span className='small-title'>{headerText}</span>
              {isNewEvaluationsBtn && (
                <button
                  onClick={handleNewEvaluationBtnClick}
                  className='button small-button primary-button'
                >
                  <img
                    alt='New Evaluation'
                    className='eval-icon'
                    src={evaluationsIcon}
                  />
                  <span>{t("buttons.new_evaluation")}</span>
                </button>
              )}
            </div>
            {isSearchBar && (
              <div className='row search-wrapper'>
                <div className='col'>
                  <div style={{ position: "relative" }}>
                    <input
                      value={searchOptions?.q}
                      onChange={(e) => updateSearchText(e.target.value)}
                      type='text'
                      className='search-input w-100'
                      placeholder={t("placeholders.search_evaluations")}
                    />
                    <img
                      alt='search-icon'
                      className='search-icon'
                      src={search}
                    />
                  </div>
                </div>
              </div>
            )}
            <div className='evaluations-list'>
              <div className='row list-label eval-list-labels-wrapper'>
                {colsToShow.includes("name") && (
                  <div
                    className={
                      colsToShow.length === 1
                        ? `col d-flex align-item-center`
                        : `col-${
                            (colsToShow.length % 3) + 3
                          } d-flex align-item-center`
                    }
                  >
                    <div
                      onClick={() => {
                        updateOrderBy(
                          searchOptions?.orderBy === "name"
                            ? "name_inv"
                            : searchOptions?.orderBy === "name_inv"
                              ? "date_inv"
                              : "name",
                        );
                      }}
                      style={{ cursor: "pointer" }}
                    >
                      {t("patient.evaluations.name_short")}
                    </div>
                    <div
                      className={`order-img-container ${
                        searchOptions?.orderBy === "name" ||
                        searchOptions?.orderBy === "name_inv"
                          ? "active"
                          : ""
                      }`}
                      onClick={() => {
                        updateOrderBy(
                          searchOptions?.orderBy === "name"
                            ? "name_inv"
                            : searchOptions?.orderBy === "name_inv"
                              ? "date_inv"
                              : "name",
                        );
                      }}
                    >
                      {searchOptions?.orderBy !== "name" && (
                        <img
                          src={
                            searchOptions?.orderBy === "name_inv"
                              ? chevronUpSelectedIcon
                              : chevronUpIcon
                          }
                          alt='Order by Name'
                        />
                      )}
                      {searchOptions?.orderBy !== "name_inv" && (
                        <img
                          src={
                            searchOptions?.orderBy === "name"
                              ? chevronDownSelectedIcon
                              : chevronDownIcon
                          }
                          alt='Order by Name Invert'
                        />
                      )}
                    </div>
                  </div>
                )}
                {colsToShow.includes("tags") && (
                  <div className='col-6'>{t("patient.evaluations.tags")}</div>
                )}
                {colsToShow.includes("date") && (
                  <div className='col d-flex align-item-center'>
                    <div
                      onClick={() =>
                        updateOrderBy(
                          searchOptions?.orderBy === "date_inv"
                            ? "date"
                            : "date_inv",
                        )
                      }
                      style={{ cursor: "pointer" }}
                    >
                      {t("patient.evaluations.date")}
                    </div>
                    <div
                      className={`order-img-container ${
                        searchOptions?.orderBy === "date" ||
                        searchOptions?.orderBy === "date_inv"
                          ? "active"
                          : ""
                      }`}
                      onClick={() =>
                        updateOrderBy(
                          searchOptions?.orderBy === "date_inv"
                            ? "date"
                            : "date_inv",
                        )
                      }
                    >
                      {searchOptions?.orderBy !== "date_inv" && (
                        <img
                          src={
                            searchOptions?.orderBy === "date"
                              ? chevronUpSelectedIcon
                              : chevronUpIcon
                          }
                          alt='Order from Old to New'
                        />
                      )}
                      {searchOptions?.orderBy !== "date" && (
                        <img
                          src={
                            searchOptions?.orderBy === "date_inv"
                              ? chevronDownSelectedIcon
                              : chevronDownIcon
                          }
                          alt='Order from New to Old'
                        />
                      )}
                    </div>
                  </div>
                )}
              </div>
              {isEvaluationListLoading ? (
                <div style={{ height: "100px" }}>
                  <div className='loading-wrapper'>
                    <Spinner type={spinnerType} />
                  </div>
                </div>
              ) : !isEvaluationListLoading &&
                patientEvaluations.obj_list.length === 0 ? (
                <div className='medium-base-color-text message-wrapper'>
                  <p>{t("patient.evaluations.no_match")}</p>
                </div>
              ) : (
                !isEvaluationListLoading &&
                patientEvaluations.obj_list.map((evaluation, index) => (
                  <div
                    key={evaluation.id + index.toString()}
                    className='row eval-info-wrapper'
                    style={{ cursor: isFullRowClickable ? "pointer" : "" }}
                    onClick={
                      isFullRowClickable
                        ? () => openEvaluation(evaluation)
                        : undefined
                    }
                  >
                    {colsToShow.includes("name") && (
                      <div
                        onClick={() => {
                          if (nameTextLink) {
                            history.push(
                              `/patient/${
                                patient_id ? patient_id : patientId
                              }/evaluations/${evaluation.id}/${nameTextLink}/`,
                            );
                          } else if (isFullRowClickable) {
                            openEvaluation(evaluation);
                          }
                        }}
                        className={
                          colsToShow.length === 1
                            ? `col regular-text black-color align-items-center pointer text-truncate`
                            : `col-${
                                (colsToShow.length % 3) + 3
                              } regular-text black-color align-items-center pointer text-truncate`
                        }
                      >
                        {evaluation.name}
                      </div>
                    )}

                    {colsToShow.includes("tags") && (
                      <div className='col-6 tags-wrapper regular-text black-color'>
                        {evaluation.tags.length === 0
                          ? "-----"
                          : evaluation.tags.map((tag) => (
                              <div key={tag.id} className='tag-container'>
                                {tag.name}
                              </div>
                            ))}
                      </div>
                    )}
                    {colsToShow.includes("date") && (
                      <div className='col regular-text black-color'>
                        {parseDateFromDatetime(evaluation.date)}
                      </div>
                    )}

                    {isFullRowClickable && (
                      <div className='col-auto'>
                        <div className='d-flex align-items-center'>
                          <div className='more-image-wrapper'>
                            <img
                              className='image-cursor'
                              onClick={() =>
                                setSelectedEvaluationId(evaluation.id)
                              }
                              src={arrowRightIcon}
                              alt='More Blue'
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {rightMenueButtons.length > 0 && !isFullRowClickable && (
                      <div className='col-auto'>
                        <div className='d-flex align-items-center'>
                          <div className='more-image-wrapper'>
                            <img
                              ref={(el) => (optionsRef.current[index] = el)}
                              className='image-cursor'
                              onMouseOver={(e) =>
                                selectedEvaluationId !== evaluation.id &&
                                (e.currentTarget.src = moreBlueSelected)
                              }
                              onMouseOut={(e) =>
                                selectedEvaluationId !== evaluation.id &&
                                (e.currentTarget.src = moreBlue)
                              }
                              onClick={(e) => {
                                e.stopPropagation();
                                selectedEvaluationId === evaluation.id
                                  ? setSelectedEvaluationId(null)
                                  : setSelectedEvaluationId(evaluation.id);
                              }}
                              src={
                                selectedEvaluationId === evaluation.id
                                  ? moreBlueSelected
                                  : moreBlue
                              }
                              alt='More Blue'
                            />
                            {selectedEvaluationId === evaluation.id && (
                              <DropdownOptions
                                ref={dropDownDivRef}
                                buttons={_renderButtons()}
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
          {!isEvaluationListLoading && isPaginator && (
            <Paginator
              currPage={patientEvaluations.page}
              totalPages={patientEvaluations.num_pages}
              maxPages={3}
              changePage={updatePage}
            />
          )}
        </div>
        {deletingEvaluationId !== null && (
          <MessageModal
            headerText={t("modals.attention_header")}
            body={t("modals.delete_evaluation_body")}
            footerButtons={[
              <button
                onClick={() => setDeletingEvaluationId(null)}
                className='small-button transparent-button'
              >
                <u>{t("buttons.cancel")}</u>
              </button>,
              <button
                onClick={() => {
                  deleteEvaluation(deletingEvaluationId);
                  setDeletingEvaluationId(null);
                }}
                className='small-button warning-button'
              >
                {t("buttons.delete")}
              </button>,
            ]}
          />
        )}
        {showCreateEvalModal && (
          <CreateEvaluationModal
            key={pid}
            patientId={pid}
            prefillClinical={prefillClinical}
            patientBirthdate={patientHome?.birthdate}
            onCancel={() => setShowCancelModal(true)}
            onSubmit={createNewEvaluation}
            isButtonDisabled={isEvaluationCreating}
            canAssignFallRisk={canAssignFallRisk}
            availableModules={availableModules}
            loadAvailableModules={loadAvailableModules}
          />
        )}

        {showCancelModal && (
          <MessageModal
            headerText={t("patient.evaluations.discard_evaluation_title")}
            body={t("patient.evaluations.discard_evaluation_body")}
            footerButtons={[
              <button
                onClick={() => setShowCancelModal(false)}
                type='button'
                className='small-button transparent-button'
              >
                <u>{t("buttons.cancel")}</u>
              </button>,
              <button
                onClick={() => {
                  setShowCancelModal(false);
                  setShowCreateEvalModal(false);
                }}
                type='button'
                className='small-button secondary-button'
              >
                <u>{t("buttons.confirm")}</u>
              </button>,
            ]}
          />
        )}
        {firstConsent && _renderFirstConsentModal()}
      </>
    )
  );
};

export default memo(EvaluationsModule);
